// Column order and headings for the executive logs, as Luigi's workbook has
// them (Leon, 2026-10-16). Client identity is never a column here: the views
// carry client ID only (0339, 0340).

export type ExecLogName =
  | "bookings"
  | "services"
  | "dispatches"
  | "giftpasses"
  | "giftpass_orders"
  | "client_wallet"
  | "partner_wallet"
  | "rider_sessions"
  | "partner_transactions"
  | "earnings_daily"
  | "wallet_daily";

export interface ExecLogColumn {
  key: string;
  label: string;
}

export const EXEC_LOG_COLUMNS: Record<ExecLogName, ExecLogColumn[]> = {
  // Luigi's BOOKING HISTORY order. "Client account name" is the client ID here.
  bookings: [
    { key: "booking_date", label: "Date" },
    { key: "booking_timestamp", label: "Timestamp" },
    { key: "booking_ref", label: "Booking reference" },
    { key: "status", label: "Status" },
    { key: "client_id", label: "Client ID" },
    { key: "subscription", label: "Subscription" },
    { key: "payment_method", label: "Payment method" },
    { key: "service_list_price", label: "Service list price" },
    { key: "surcharge", label: "Surcharge" },
    { key: "giftpass_used", label: "GiftPass used" },
    { key: "wallet_used", label: "Wallet used" },
    { key: "promo_discount", label: "Promo discount" },
    { key: "summary_total", label: "Summary total" },
    { key: "online_processing", label: "Online processing" },
    { key: "cancel_fee", label: "Cancel fee" },
    { key: "amount_paid", label: "Amount paid" },
  ],
  // Luigi's SERVICE HISTORY order. "Account / client name" is the client ID here.
  services: [
    { key: "service_ref", label: "Service reference" },
    { key: "therapist_name", label: "Therapist name" },
    { key: "mt_code", label: "MT code" },
    { key: "payment_method", label: "Payment method" },
    { key: "booking_ref", label: "Booking reference" },
    { key: "client_id", label: "Client ID" },
    { key: "status", label: "Status" },
    { key: "service_name", label: "Service type" },
    { key: "service_list_price", label: "Service list price" },
    { key: "therapist_share", label: "Therapist share" },
    { key: "surcharge_share", label: "Surcharge share" },
    { key: "cancellation_share", label: "Cancelation share" },
  ],
  // Luigi's DISPATCH HISTORY order. His three addresses stay out (they lead to
  // a client's home); booking, client and distance source are ours, hidden.
  dispatches: [
    { key: "dispatch_date", label: "Date" },
    { key: "dispatch_timestamp", label: "Timestamp" },
    { key: "rider_name", label: "Rider name" },
    { key: "rider_id", label: "Rider code" },
    { key: "rider_tier", label: "Tier" },
    { key: "dispatch_ref", label: "Dispatch reference" },
    { key: "therapist_name", label: "Therapist name" },
    { key: "status", label: "Status" },
    { key: "route_km", label: "Route distance (km)" },
    { key: "total_km", label: "Total dispatch distance (km)" },
    { key: "booking_ref", label: "Booking reference" },
    { key: "client_id", label: "Client ID" },
    { key: "route_source", label: "Distance source" },
  ],
  // Luigi's GIFTPASS TRACKER sheet, his 15 columns in his order (0384). His
  // "Buyer name" is the order reference and "Claimed by" a client ID here.
  giftpasses: [
    { key: "giftpass_ref", label: "Reference number" },
    { key: "value_php", label: "Value" },
    { key: "printed", label: "Printed" },
    { key: "status", label: "Status" },
    { key: "order_ref", label: "Buyer (order)" },
    { key: "sold_by", label: "Sold by (seller code)" },
    { key: "date_sold", label: "Date sold" },
    { key: "claimed_by_id", label: "Claimed by (client ID)" },
    { key: "date_claimed", label: "Date claimed" },
    { key: "date_generated", label: "Date generated" },
    { key: "card_count", label: "Count" },
    { key: "generation_batch", label: "Generation batch" },
    { key: "year_generated", label: "Year generated" },
    { key: "card_layout", label: "Card layout" },
    { key: "full_reference", label: "Full GiftPass reference" },
    { key: "used_on_booking", label: "Used on booking" },
  ],
  // Luigi's GIFTPASS PACKAGE LOGS sheet in his order, without name, contact,
  // email and address (0384). The last three are ours, hidden by default.
  giftpass_orders: [
    { key: "order_timestamp", label: "Timestamp" },
    { key: "order_ref", label: "Buyer (order)" },
    { key: "package_name", label: "Package" },
    { key: "total_amount", label: "Total amount" },
    { key: "seller_code", label: "Seller code" },
    { key: "payment_method", label: "Payment method" },
    { key: "payment_reference", label: "Reference no" },
    { key: "has_proof", label: "Proof attached" },
    { key: "payment_verification", label: "Payment verification" },
    { key: "delivery_status", label: "Delivery status" },
    { key: "quantity", label: "Quantity" },
    { key: "processing_fee", label: "Processing fee" },
    { key: "net_amount", label: "Net amount" },
  ],
  // Luigi's CLIENT WALLET HISTORY order. Client name, contact and email stay out.
  client_wallet: [
    { key: "entry_id", label: "Transaction ID" },
    { key: "entry_date", label: "Date" },
    { key: "entry_timestamp", label: "Time" },
    { key: "client_id", label: "Client ID" },
    { key: "entry_type", label: "Type" },
    { key: "amount", label: "Amount" },
    { key: "balance_after", label: "Balance after" },
    { key: "booking_ref", label: "Booking reference" },
    { key: "topup_id", label: "Top-up reference" },
    { key: "note", label: "Note" },
  ],
  partner_wallet: [
    { key: "staff_id", label: "Partner code" },
    { key: "staff_name", label: "Partner name" },
    { key: "role", label: "Role" },
    { key: "posted_earnings", label: "Posted earnings" },
    { key: "cash_held", label: "Cash held" },
    { key: "payout_held", label: "Payout pending" },
    { key: "cash_retained", label: "Cash retained" },
    { key: "payout_paid", label: "Paid out" },
    { key: "available_balance", label: "Available balance" },
  ],
  // Luigi's PARTNER WALLET HISTORY: one row per movement.
  partner_transactions: [
    { key: "entry_timestamp", label: "Date / time" },
    { key: "staff_id", label: "Partner code" },
    { key: "staff_name", label: "Partner" },
    { key: "role", label: "Role" },
    { key: "entry_type", label: "Entry type" },
    { key: "reference", label: "Reference" },
    { key: "amount", label: "Amount" },
    { key: "entry_date", label: "Date" },
  ],
  rider_sessions: [
    { key: "rider_session_id", label: "Session reference" },
    { key: "computed_at", label: "Computed at" },
    { key: "rider_name", label: "Rider name" },
    { key: "rider_id", label: "Rider code" },
    { key: "rider_tier", label: "Tier" },
    { key: "dispatch_count", label: "Dispatches" },
    { key: "total_km", label: "Distance (km)" },
    { key: "base_php", label: "Base fare" },
    { key: "peak_incentive_php", label: "Peak incentive" },
    { key: "extension_fee_php", label: "Extension fee" },
    { key: "guarantee_php", label: "Guarantee" },
    { key: "cancellation_fees_php", label: "Cancellation fees" },
    { key: "surcharge_shares_php", label: "Surcharge shares" },
    { key: "total_php", label: "Total pay" },
  ],
  // 0392: what each partner earned per day, therapists split by how the client
  // paid. Riders are paid per Session, so their pay is one column, not split.
  earnings_daily: [
    { key: "earned_on", label: "Date" },
    { key: "staff_id", label: "Partner ID" },
    { key: "staff_name", label: "Partner name" },
    { key: "role", label: "Role" },
    { key: "bookings", label: "Bookings" },
    { key: "rider_sessions", label: "Rider sessions" },
    { key: "service_online", label: "Earned, online or prepaid" },
    { key: "service_cash", label: "Earned, cash" },
    { key: "shares", label: "Surcharge and cancellation shares" },
    { key: "rider_pay", label: "Rider pay" },
    { key: "total_earned", label: "Total earned" },
    { key: "cash_received", label: "Company cash received" },
  ],
  // 0392: the app wallet per day. End of day is before any payout still pending.
  wallet_daily: [
    { key: "entry_date", label: "Date" },
    { key: "staff_id", label: "Partner ID" },
    { key: "staff_name", label: "Partner name" },
    { key: "role", label: "Role" },
    { key: "opening_balance", label: "Start of day" },
    { key: "earnings", label: "Earnings in" },
    { key: "cash_movement", label: "Cash held or handed over" },
    { key: "withdraw_requested", label: "Withdrawal requested" },
    { key: "withdrawn", label: "Withdrawn (paid)" },
    { key: "closing_balance", label: "End of day" },
  ],
};

export const EXEC_LOG_TITLES: Record<ExecLogName, string> = {
  bookings: "Booking history",
  services: "Service history",
  dispatches: "Dispatch history",
  giftpasses: "GiftPass tracker",
  giftpass_orders: "GiftPass package logs",
  client_wallet: "Client wallet history",
  partner_wallet: "Partner balances",
  rider_sessions: "Rider session pay",
  partner_transactions: "Partner wallet history",
  earnings_daily: "Earnings daily",
  wallet_daily: "Wallet daily",
};
